import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Download, Sparkles, CheckCircle2, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * LeadMagnetForm — drops into the bottom of cornerstone blog posts to
 * convert a reader into an email subscriber + PDF download.
 *
 * Design intent:
 *   - Brand-consistent gradient (emerald → cyan), not a generic "newsletter" CTA.
 *   - Single email field (zero friction).
 *   - Immediate PDF download AFTER submit (no wait for email delivery).
 *   - Confirmation email also fires server-side (founder gets to follow up).
 *
 * Props:
 *   slug      — lead-magnet identifier (e.g. "ec2-right-sizing-checklist")
 *   postSlug  — post that contains this form (for funnel attribution)
 */
export const LeadMagnetForm = ({ slug, postSlug }) => {
  const [manifest, setManifest] = useState(null);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null); // { downloadUrl, title }

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/lead-magnets/${slug}/manifest`).then((r) => {
      if (!cancelled) setManifest(r.data);
    }).catch(() => {
      // Silently no-op — the form just won't render.
      if (!cancelled) setManifest(null);
    });
    return () => { cancelled = true; };
  }, [slug]);

  if (!manifest) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/lead-magnets/subscribe`, {
        email: email.trim(),
        magnet_slug: slug,
        post_slug: postSlug || manifest.post_slug,
      });
      if (r.data?.ok && r.data?.download_url) {
        setSuccess({
          downloadUrl: `${process.env.REACT_APP_BACKEND_URL}${r.data.download_url}`,
          title: r.data.title,
        });
        toast.success('Sent! Check your inbox + download below.');
      } else {
        toast.error('Could not sign you up. Please try again.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not sign you up.');
    } finally {
      setSubmitting(false);
    }
  };

  // Success state — pure download CTA, no form.
  if (success) {
    return (
      <div
        data-testid={`lead-magnet-success-${slug}`}
        className="my-10 p-6 rounded-2xl border border-emerald-400/30 bg-slate-900 bg-gradient-to-br from-emerald-950/40 via-slate-900 to-cyan-950/30"
      >
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 shrink-0 rounded-full bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center">
            <CheckCircle2 className="w-5 h-5 text-emerald-300" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-emerald-300 mb-1">
              You're in
            </p>
            <h3 className="text-lg font-bold text-white">
              {success.title} is ready
            </h3>
            <p className="text-sm text-slate-300/90 mt-1.5 leading-relaxed">
              We also sent a copy to <b>{email}</b> — reply to that email if you want
              the founder to take a look at your bill personally.
            </p>
            <a
              href={success.downloadUrl}
              data-testid={`lead-magnet-download-${slug}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 mt-4 px-5 py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-sm transition-colors shadow-md shadow-emerald-500/30"
            >
              <Download className="w-4 h-4" />
              Download the PDF
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form
      data-testid={`lead-magnet-form-${slug}`}
      onSubmit={submit}
      className="my-10 p-6 sm:p-8 rounded-2xl border border-emerald-400/25 bg-slate-900 bg-gradient-to-br from-emerald-950/40 via-slate-900 to-cyan-950/20 shadow-[0_0_60px_-15px_rgba(16,185,129,0.35)]"
    >
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 shrink-0 rounded-xl bg-gradient-to-br from-emerald-500/30 to-cyan-500/15 border border-emerald-400/40 flex items-center justify-center">
          <Sparkles className="w-5 h-5 text-emerald-300" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-emerald-300 mb-1.5">
            Free · Printable · Ready to run
          </p>
          <h3 className="text-xl sm:text-2xl font-bold text-white tracking-tight leading-tight">
            Get the {manifest.title}
          </h3>
          <p className="text-sm text-slate-300/90 mt-2 leading-relaxed max-w-xl">
            {manifest.tagline}
          </p>
          <div className="mt-5 flex flex-col sm:flex-row gap-2.5 max-w-xl">
            <Input
              data-testid={`lead-magnet-email-input-${slug}`}
              type="email"
              required
              placeholder="you@yourcompany.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 bg-slate-950 border-slate-700 text-white placeholder:text-slate-400 h-11"
              disabled={submitting}
            />
            <Button
              type="submit"
              data-testid={`lead-magnet-submit-${slug}`}
              disabled={submitting}
              className="h-11 px-5 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold border-0 shadow-md shadow-emerald-500/30 group"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending…
                </>
              ) : (
                <>
                  Get the PDF
                  <ArrowRight className="w-4 h-4 ml-1.5 transition-transform group-hover:translate-x-0.5" />
                </>
              )}
            </Button>
          </div>
          <p className="text-[11px] text-slate-400 mt-3">
            No spam. The founder reads every reply personally.
          </p>
        </div>
      </div>
    </form>
  );
};

export default LeadMagnetForm;
